import { Navigate, useNavigate } from "react-router-dom";
import { Show, UserButton } from "@clerk/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthed } from "../lib/adminApi";
import { lineDetails } from "../lib/cart";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { XCircle, Clock, ChefHat, Truck, PackageCheck, RefreshCw, Phone, MapPin } from "lucide-react";
import MenuManager from "../components/admin/MenuManager";
import WebsiteMessages from "../components/admin/WebsiteMessages";
import SpinAdmin from "../components/admin/SpinAdmin";
import ExtrasManager from "../components/admin/ExtrasManager";
import EmailSettings from "../components/admin/EmailSettings";
import { toast } from "sonner";

const STATUS_META = {
  received: { label: "Received", color: "bg-amber-100 text-amber-800", icon: Clock, next: "preparing" },
  preparing: { label: "Preparing", color: "bg-blue-100 text-blue-800", icon: ChefHat, next: "out_for_delivery" },
  out_for_delivery: { label: "Out for Delivery", color: "bg-purple-100 text-purple-800", icon: Truck, next: "delivered" },
  delivered: { label: "Delivered", color: "bg-emerald-100 text-emerald-800", icon: PackageCheck, next: null },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800", icon: XCircle, next: null },
};
const COLUMNS = ["received", "preparing", "out_for_delivery", "delivered", "cancelled"];

function AdminShell({ children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#F6F6F3]">
      <header className="bg-[#111111] text-white sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="flex items-center gap-2">
            <img src="/logo192.png" alt="MLOBOX logo" data-testid="admin-brand-logo" className="h-9 w-9 rounded-xl" />
            <div>
              <div className="font-display font-extrabold text-base leading-none">MLOBOX</div>
              <div className="text-[10px] text-white/60">Admin console</div>
            </div>
          </button>
          <div className="ml-auto flex items-center gap-3">
            <a href="/" className="text-sm text-white/70 hover:text-[#FF8A3D]" data-testid="go-storefront">Storefront</a>
            <UserButton afterSignOutUrl="/" />
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto p-4 sm:p-6">{children}</main>
    </div>
  );
}

function OrderCard({ o, meta, setStatus }) {
  return (
    <div className="rounded-xl border border-gray-100 p-3 bg-[#FAF9F6]" data-testid={`order-card-${o.code}`}>
      <div className="flex items-center justify-between">
        <div className="font-semibold text-sm">{o.code}</div>
        <Badge className={`${meta.color} border-0 text-[10px]`}>{meta.label}</Badge>
      </div>
      <div className="text-xs text-gray-600 mt-1">{o.customer_name}</div>
      <div className="text-[11px] text-gray-500 flex items-center gap-1"><Phone size={10} /> {o.phone}</div>
      <div className="text-[11px] text-gray-500 flex items-center gap-1"><MapPin size={10} /> {o.address}, {o.zone}</div>
      {o.lat != null && (
        <a href={`https://www.google.com/maps/dir/?api=1&destination=${o.lat},${o.lng}`} target="_blank" rel="noreferrer"
          className="text-[11px] font-semibold text-[#CC1A00] underline" data-testid={`directions-${o.code}`}>Open pin in Google Maps</a>
      )}
      <ul className="text-[11px] mt-1.5 text-gray-700 space-y-0.5">
        {o.items.map((it, i) => (
          <li key={i}>• {it.quantity}× {it.name}{lineDetails(it) && <span className="text-gray-500"> ({lineDetails(it)})</span>}</li>
        ))}
      </ul>
      {o.instructions && <div className="text-[11px] mt-1 italic text-gray-500">“{o.instructions}”</div>}
      <div className="flex items-center justify-between mt-2">
        <div className="font-bold text-[#CC1A00] text-sm">KSh {o.total}</div>
        <div className="text-[10px] uppercase tracking-wide text-gray-500">{o.payment_method} · {o.payment_status}</div>
      </div>
      {meta.next && (
        <>
          <Button size="sm" className="w-full mt-2 mlobox-gradient text-white rounded-full h-8" onClick={() => setStatus(o, meta.next)} data-testid={`advance-${o.code}`}>
            Mark as {STATUS_META[meta.next].label}
          </Button>
          <Button size="sm" variant="ghost" className="w-full mt-1 text-red-600 hover:bg-red-50 rounded-full h-7 text-xs" onClick={() => { if (window.confirm(`Cancel order ${o.code}?`)) setStatus(o, "cancelled"); }} data-testid={`cancel-${o.code}`}>
            Cancel order
          </Button>
        </>
      )}
    </div>
  );
}

function OrdersBoard() {
  const call = useAuthed();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await call("get", "/admin/orders"); setOrders(r.data); } catch { /* retried on next poll */ } finally { setLoading(false); }
  }, [call]);

  useEffect(() => { load(); const t = setInterval(load, 7000); return () => clearInterval(t); }, [load]);

  const setStatus = async (order, status) => {
    try {
      await call("patch", `/admin/orders/${order.id}/status`, { status });
      toast.success(`Order ${order.code} → ${STATUS_META[status]?.label}`);
      load();
    } catch { toast.error("Failed to update status"); }
  };

  const groups = useMemo(() => {
    const g = Object.fromEntries(COLUMNS.map((c) => [c, []]));
    orders.forEach((o) => { if (g[o.status]) g[o.status].push(o); });
    return g;
  }, [orders]);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="font-display font-extrabold text-xl sm:text-2xl">Live Orders</h1>
        <Button variant="outline" size="sm" onClick={load} data-testid="refresh-orders"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
      </div>
      {loading && <div className="text-gray-500 py-10 text-center">Loading…</div>}
      <div className="grid md:grid-cols-3 xl:grid-cols-5 gap-4">
        {COLUMNS.map((col) => {
          const meta = STATUS_META[col];
          const Icon = meta.icon;
          return (
            <section key={col} className="bg-white rounded-2xl border border-gray-100 p-3" data-testid={`orders-col-${col}`}>
              <div className="flex items-center gap-2 px-1 pb-2">
                <Icon size={14} className="text-gray-500" />
                <div className="font-display font-bold text-sm">{meta.label}</div>
                <span className="ml-auto text-xs text-gray-400">{groups[col].length}</span>
              </div>
              <div className="space-y-2">
                {groups[col].length === 0 && <div className="text-xs text-gray-400 px-2 py-6 text-center">No orders</div>}
                {groups[col].map((o) => <OrderCard key={o.id} o={o} meta={meta} setStatus={setStatus} />)}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <>
      <Show when="signed-out"><Navigate to="/admin/sign-in" replace /></Show>
      <Show when="signed-in">
        <AdminGate />
      </Show>
    </>
  );
}

function AdminGate() {
  const call = useAuthed();
  const [role, setRole] = useState(null);
  useEffect(() => {
    call("get", "/me").then((r) => setRole(r.data.role)).catch(() => setRole("none"));
  }, [call]);

  if (role === null) return <div className="min-h-screen grid place-items-center text-gray-500">Checking access…</div>;
  if (role !== "admin") return (
    <div className="min-h-screen grid place-items-center p-8 text-center">
      <div className="max-w-md">
        <h1 className="font-display text-2xl font-bold">You&apos;re signed in, but not an admin.</h1>
        <p className="text-gray-500 mt-2">This account doesn&apos;t have admin access. Ask the restaurant owner to add your email as an admin.</p>
        <Button className="mt-4" onClick={() => (window.location.href = "/")}>Back to menu</Button>
      </div>
    </div>
  );

  return (
    <AdminShell>
      <Tabs defaultValue="orders">
        <TabsList className="h-auto max-w-full overflow-x-auto justify-start">
          <TabsTrigger value="orders" data-testid="tab-orders">Live Orders</TabsTrigger>
          <TabsTrigger value="menu" data-testid="tab-menu">Menu</TabsTrigger>
          <TabsTrigger value="website" data-testid="tab-website">Website</TabsTrigger>
          <TabsTrigger value="spin" data-testid="tab-spin">Spin &amp; Win</TabsTrigger>
          <TabsTrigger value="extras" data-testid="tab-extras">Extras</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-5"><OrdersBoard /></TabsContent>
        <TabsContent value="website" className="mt-5"><WebsiteMessages /></TabsContent>
        <TabsContent value="spin" className="mt-5"><SpinAdmin /></TabsContent>
        <TabsContent value="menu" className="mt-5"><MenuManager /></TabsContent>
        <TabsContent value="extras" className="mt-5"><ExtrasManager /></TabsContent>
        <TabsContent value="email" className="mt-5"><EmailSettings /></TabsContent>
      </Tabs>
    </AdminShell>
  );
}
